import { Grid, Switch } from '@mui/material';
import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import { Project } from '../../../domain/project/Project';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import { useUpdateProjectCustomField } from '../../../hooks/project/useUpdateProjectCustomField';
import { ObjectOfAny } from '../../../interfaces/common';
import Text from '../../../react-components/Text';
import { DataTable } from '../../../react-components/data-table/DataTable';
import { ProjectCustomFieldTypeIcon } from '../custom_fields/ProjectCustomFieldIcons';
import { Sections, getProjectFieldsBySection, sectionsProjectFields } from './ProjectFields';

type GetDescriptionFieldsProps = {
  type: Sections;
  project: Project;
  showCustomFields?: boolean;
  descriptionFields: ObjectOfAny;
  emptyFieldsIntlId?: string;
};

export const GetDescriptionFields = ({
  type,
  project,
  showCustomFields = false,
  descriptionFields,
  emptyFieldsIntlId,
}: GetDescriptionFieldsProps) => {
  const { run: updateProject } = useUpdateProject({ project });
  const { run: updateCustomField } = useUpdateProjectCustomField();

  type ProjectCustomFieldDefinitionItem = CustomFieldDefinition & {
    description: string;
    settingType: `custom_field`;
  };

  type ProjectFieldItem = {
    id: string;
    // setting: string;
    description: string;
    settingType: `description`;
  };

  const docs = [
    ...(getProjectFieldsBySection(type).map((field: string) => ({
      id: field,
      description: (sectionsProjectFields[type] as any)[field].description,
      settingType: `description`,
    })) as Array<ProjectFieldItem>),
    ...((showCustomFields && (project.custom_fields as Record<string, any>)[type].length > 0
      ? (project.custom_fields as Record<string, any>)[type].map((field: CustomFieldDefinition) => ({
          ...field.getValue(),
          fieldType: field.field_type,
          settingType: `custom_field`,
        }))
      : []) as Array<ProjectCustomFieldDefinitionItem>),
  ] as Array<ProjectFieldItem | ProjectCustomFieldDefinitionItem>;

  return (
    <Grid
      container
      direction="column"
      style={{
        padding: `16px 22px`,
      }}
    >
      <Grid container direction="column" wrap="nowrap">
        {docs.length > 0 ? (
          <DataTable
            noFooter
            total={docs.length}
            showCheckboxes={false}
            docs={[
              ...getProjectFieldsBySection(type).map((field) => {
                const projectFieldByType = sectionsProjectFields[type];

                const { description } = (projectFieldByType as any)[field];

                return {
                  doc: {
                    id: field,
                    description,
                    settingType: `description`,
                  },
                };
              }),
              ...(showCustomFields && (project.custom_fields as Record<string, any>)[type].length > 0
                ? (project.custom_fields as Record<string, any>)[type].map((field: CustomFieldDefinition) => ({
                    doc: {
                      ...field.getValue(),
                      fieldType: field.field_type,
                      settingType: `custom_field`,
                    },
                  }))
                : []),
            ]}
            columns={[
              {
                width: `calc(100% - 135px)`,
                accessorName: `name`,
                name: (
                  <Text
                    style={{ color: RoutalPalette.primary20 }}
                    isBold
                    intlId="projects.settings.fields.column.name"
                  />
                ),
                formatter: ({ doc }: { doc: ProjectFieldItem | ProjectCustomFieldDefinitionItem }) => {
                  return (
                    <Grid container direction="column" wrap="nowrap">
                      <Grid item container direction="row">
                        <Grid item container style={{ width: `unset` }} alignItems="center">
                          {doc.settingType === `description` ? (
                            <img
                              alt=""
                              src={(sectionsProjectFields[type] as any)[doc.id].iconSrc}
                              style={{ width: `12px`, height: `12px` }}
                            />
                          ) : (
                            <ProjectCustomFieldTypeIcon
                              fieldType={(doc as ProjectCustomFieldDefinitionItem).field_type}
                            />
                          )}
                        </Grid>

                        <HSpacer small />

                        <Grid item>
                          {doc.settingType === `custom_field` ? (
                            <>
                              {doc.description ? (
                                <RoutalTooltip
                                  id={`field-description-${(doc as ProjectCustomFieldDefinitionItem).custom_id}`}
                                  placement="top"
                                  overlayStyle={{
                                    maxWidth: `300px`,
                                  }}
                                  title={doc.description}
                                >
                                  {doc.label ? (
                                    <Text variant="body-large">{doc.label}</Text>
                                  ) : (
                                    <Text variant="body-large" intlId="no_name" />
                                  )}
                                </RoutalTooltip>
                              ) : (
                                <>
                                  {doc.label ? (
                                    <Text variant="body-large">{doc.label}</Text>
                                  ) : (
                                    <Text variant="body-large" intlId="no_name" />
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {doc.description ? (
                                <RoutalTooltip
                                  id="plan-id"
                                  placement="top"
                                  title={intl.formatMessage({ id: doc.description })}
                                >
                                  <Text variant="body-large" intlId={`projects.settings.fields.${type}.${doc.id}`} />
                                </RoutalTooltip>
                              ) : (
                                <Text variant="body-large" intlId={`projects.settings.fields.${type}.${doc.id}`} />
                              )}
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                },
              },
              {
                accessorName: `enabled`,
                align: `right`,
                name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="enabled" />,
                formatter: ({ doc }: { doc: ProjectFieldItem | ProjectCustomFieldDefinitionItem }) => (
                  <Grid container>
                    <Grid item>
                      {doc.settingType === `custom_field` ? (
                        <Switch
                          checked={doc.enabled || false}
                          onChange={(event: any) => {
                            const { checked } = event.target;
                            updateCustomField({
                              customField: doc as CustomFieldDefinition,
                              enabled: checked,
                            });
                          }}
                          color="primary"
                        />
                      ) : (
                        <Switch
                          checked={(descriptionFields as Record<string, any>)[type][doc.id] || false}
                          onChange={(event: any) => {
                            const { checked } = event.target;

                            // @ts-expect-error
                            project.view[type][`${doc.id}`] = checked;

                            updateProject({
                              view: project.view,
                            });
                          }}
                          color="primary"
                        />
                      )}
                    </Grid>
                  </Grid>
                ),
              },
              // ...(showCustomFields
              //   ? [
              //       {
              //         accessorName: `actions`,
              //         align: `right` as AlignmentTypes,
              //         formatter: ({ doc }: { doc: ProjectFieldItem | ProjectCustomFieldDefinitionItem }) => (
              //           <div style={{ position: `absolute`, right: 0, marginTop: `-16px` }}>
              //             {doc.settingType === `custom_field` ? (
              //               <EditRemoveMenu
              //                 id={`custom_field-${(doc as ProjectCustomFieldDefinitionItem).custom_id}-actions`}
              //                 onEdit={() => {
              //                   setCurrentField(doc);
              //                 }}
              //                 onDelete={() => {
              //                   setConfirmDeleteCustomFieldId(``);
              //                   setFieldToRemove({
              //                     projectId: project.id,
              //                     customId: (doc as ProjectCustomFieldDefinitionItem).custom_id,
              //                     type,
              //                   });
              //                   setConfirmDeleteCustomFieldDialog(true);
              //                 }}
              //               />
              //             ) : null}
              //           </div>
              //         ),
              //       },
              //     ]
              //   : []),
            ]}
          />
        ) : (
          <>
            {emptyFieldsIntlId ? (
              <Grid item container wrap="nowrap" justifyContent="center" alignItems="center" style={{ height: `60px` }}>
                <Text variant="body-large" intlId={emptyFieldsIntlId} />
              </Grid>
            ) : null}
          </>
        )}
      </Grid>
    </Grid>
  );
};
